import type { TelegramUser } from "@/types/User";
import { authHeader } from "@roman/shared/lib/auth/authHeader";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFetchUser } from "./useFetchUserApi";
import { useHandleApiError } from "./useHandleApiError";
import { useLoginWithTelegram } from "./useLoginWithTelegramApi";
import { useTelegram } from "./useTelegram";
import { useUpdateUser } from "./useUpdateUserApi";

interface Telegram {
  initDataRaw: string;
  telegramUserId: number;
  telegramUserName?: string;
  photoUrl?: string;
}

export const useInitAuth = () => {
  const navigate = useNavigate();
  const isInitializedRef = useRef(false);
  const [user, setUser] = useState<TelegramUser | null>(null);

  const { handleError } = useHandleApiError();

  const telegram = useTelegram();

  const fetchUserApi = useFetchUser({
    onSuccess: (response) => {
      if (response.user.type !== "TELEGRAM") {
        throw new Error("User is not a telegram user");
      }
      setUser(response.user);
    },
    onError: handleError("Error when fetching user"),
  });
  const updateUserApi = useUpdateUser({
    onError: handleError("Error when logging in with telegram"),
  });
  const telegramLoginApi = useLoginWithTelegram({
    onError: handleError("Error when logging in with telegram"),
  });

  // Note: This Effect is executed only once at initialization
  const init = async ({ telegramUserId, initDataRaw, photoUrl, telegramUserName }: Telegram) => {
    // 1. Login with telegram
    const loginResponse = await telegramLoginApi.trigger({
      token: initDataRaw,
    });
    if (loginResponse.isFirstTime) {
      navigate("/tutorial");
    }
    authHeader.set(loginResponse.accessToken);
    // 2. Fetch user from API
    const { user } = await fetchUserApi.mutation.trigger({ telegramUserId });
    // 3. Update user if needed
    if (user.photoUrl !== photoUrl || user.telegramUserName !== telegramUserName) {
      await updateUserApi.trigger({
        userId: user.id,
        photoUrl: photoUrl,
        telegramUserName: telegramUserName,
      });
      // refetch user
      await fetchUserApi.mutation.trigger({ telegramUserId });
    }
    // 4. Start polling for user
    fetchUserApi.stopPolling(); // Stop existing polling just in case
    fetchUserApi.startPolling(telegramUserId);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: function is called only once
  useEffect(() => {
    if (!isInitializedRef.current && telegram.telegramUserId && telegram.initDataRaw) {
      isInitializedRef.current = true;
      init({
        telegramUserId: telegram.telegramUserId,
        initDataRaw: telegram.initDataRaw,
        photoUrl: telegram.photoUrl,
        telegramUserName: telegram.telegramUserName,
      });
    }

    // Cleanup
    return () => {
      fetchUserApi.stopPolling();
    };
  }, [telegram.telegramUserId, telegram.initDataRaw]);

  return {
    user,
  };
};
